import { DateTime } from "luxon"
import * as FieldCode from "./constants"
import { convertStringToDateTime } from "./dateTimeParsing"
import { parseFieldData } from "./fieldparsing"
import {
    ChangedBy,
    ChangedOn,
    PageFooter,
} from "../../components/itemdetail/components/pageFooter"
import { IItemStatus } from "../../models"
import { MetaDataDTO } from "../models/fields"
import { ODataGetResourceResponse } from "../models/api/data"
import { StatusDTO } from "../models/dto/concept"
import { User } from "../models/user"
import { IItemDetail, IMetaData } from "../../components/itemdetail"
import { PageHeader } from "../../components/itemdetail/components/pageHeader/pageHeader"
import { ITextField } from "../../components/fields"
import { IUserFormattingSettings } from "../../components/applicationheader/components/usermenu"

export const parseMetaData = (component: MetaDataDTO): IMetaData => {
    return {
        label: component.ComponentDefinition.Label,
        value: component.Field.Value,
        description: component.ComponentDefinition.Description,
        isRequired: component.Field.IsRequired,
        itemId: component.ItemId,
        componentCode: component.ComponentCode,
    }
}

export const extractMetaData = (
    components: MetaDataDTO[],
    userFormattingSettings: IUserFormattingSettings,
): Omit<IItemDetail, "tabs" | "item"> => {
    const metaData: IMetaData[] = []
    const footer: PageFooter = {} as PageFooter
    const header: PageHeader = {} as PageHeader

    for (const component of components) {
        if (!component.Field.Value) {
            metaData.push(parseMetaData(component))
            continue
        }
        switch (component.ComponentCode) {
            case FieldCode.TitleCode:
                header.title = parseFieldData(
                    component,
                    userFormattingSettings,
                ) as ITextField
                break
            case FieldCode.CreatorComponentCode:
                footer.createdBy = parsePageFooterEntity(component)
                break
            case FieldCode.CreatedOnComponentCode:
                footer.createdOn = parsePageFooterDateTime(component)
                break
            case FieldCode.LastUpdatedByComponentCode:
                footer.lastModifiedBy = parsePageFooterEntity(component)
                break
            case FieldCode.LastUpdateComponentCode:
                footer.lastModifiedOn = parsePageFooterDateTime(component)
                break
            case FieldCode.CreatedOnBehalfOfComponentCode:
                footer.createdOnBehalfOf = parsePageFooterEntity(component)
                break
            case FieldCode.LastUpdatedOnBehalfOfComponentCode:
                footer.modifiedOnBehalfOf = parsePageFooterEntity(component)
                break
            default:
                metaData.push(parseMetaData(component))
                break
        }
    }
    return { footer, metaData, header }
}

export const parsePageFooterEntity = (component: MetaDataDTO): ChangedBy => {
    return {
        label: component.ComponentDefinition.Label,
        user: {
            label: (component.Field.Value as User).Label,
            firstName: (component.Field.Value as User).FirstName,
            lastName: (component.Field.Value as User).LastName,
        },
    }
}

export const parsePageFooterDateTime = (component: MetaDataDTO): ChangedOn => {
    const dateTime =
        convertStringToDateTime(component.Field.Value as string) ||
        DateTime.now()
    return {
        label: component.ComponentDefinition.Label,
        date: dateTime.toFormat("dd MMMM yyyy"),
        time: dateTime.toFormat("HH:mm"),
    }
}

export const parseStatusDto = (
    dto: StatusDTO | ODataGetResourceResponse<StatusDTO>,
): IItemStatus => ({
    code: dto.Code,
    id: dto.Id,
    isActive: dto.IsActive,
    isIndicatingItemIsActive: dto.IndicatesItemIsActive,
    label: dto.Label,
    sequenceNr: dto.SequenceNr,
})
